import { PJSONPOptions } from 'pjsonp';
import { AdditionalItems } from '../interfaces/search/AdditionalItems';
import { ContactItem } from '../interfaces/search/additionalItems/ContactItem';
import { CustomItem } from '../interfaces/search/additionalItems/CustomItem';
import { FreeTextItem } from '../interfaces/search/additionalItems/FreeTextItem';
import { GenericItem } from '../interfaces/search/additionalItems/GenericItem';
import { HomeItem } from '../interfaces/search/additionalItems/HomeItem';
import { NewsItem } from '../interfaces/search/additionalItems/NewsItem';
import { PrintItem } from '../interfaces/search/additionalItems/PrintItem';
import { ProductItem } from '../interfaces/search/additionalItems/ProductItem';
import { VideoItem } from '../interfaces/search/additionalItems/VideoItem';
import { ClusterEntry } from '../interfaces/search/ClusterEntry';
import { ResultEntry } from '../interfaces/search/ResultEntry';
import { SearchEntries } from '../interfaces/search/SearchEntries';
import { SearchEntry } from '../interfaces/search/SearchEntry';
import {
  Credentials,
  ResponseError
} from '../interfaces/webservices/core-api-v2/General';
import {
  AddressItem,
  AddressSearchResult,
  AddressTile,
  IMAGE_TYPE,
  OBJECT_LIST_TYPE
} from '../interfaces/webservices/core-api-v2/Response/AddressSearches';
import {
  IMAGE_FORMAT,
  ObjectListItem
} from '../interfaces/webservices/core-api-v2/Response/AddressSearches';
import { isNotEmptyString } from './guards';
import { normalizeOpeningHours } from './OpeningHours';
import { RequestGeocoder } from './RequestGeocoder';
import { latLngNeqZero } from './util';
import { getConfig } from '../ConfigLoader';
import { IConfig } from '../presets/IConfig';

export class RequestSearch {
  static config: IConfig;

  static BCs: string[] = [];

  static ImageFormat: { [key: string]: string } = {
    [IMAGE_FORMAT.GIF]: 'image/gif',
    [IMAGE_FORMAT.JPG]: 'image/jpeg',
    [IMAGE_FORMAT.PNG]: 'image/png',
    [IMAGE_FORMAT.JPEG]: 'image/jpeg'
  };

  static AdditionalInfo: { [key: string]: string } = {
    [OBJECT_LIST_TYPE.NONE]: 'custom',
    [OBJECT_LIST_TYPE.PRODUCTS]: 'product',
    [OBJECT_LIST_TYPE.NEWS]: 'news',
    [OBJECT_LIST_TYPE.HOME]: 'home',
    [OBJECT_LIST_TYPE.CONTACT]: 'contact',
    [OBJECT_LIST_TYPE.CUSTOM]: 'custom',
    [OBJECT_LIST_TYPE.CHAPTER]: 'chapter',
    [OBJECT_LIST_TYPE.FREETEXT]: 'freeText',
    [OBJECT_LIST_TYPE.PRINT]: 'print',
    [OBJECT_LIST_TYPE.PARTNERS]: 'partner',
    [OBJECT_LIST_TYPE.PROMOTIONS]: 'promotion',
    [OBJECT_LIST_TYPE.VIDEOS]: 'video',
    [OBJECT_LIST_TYPE.COUPONS]: 'coupon',
    [OBJECT_LIST_TYPE.INFOTEXT]: 'infoText'
  };

  static convertTime(time: string): Date {
    return new Date(time.replace(' ', 'T') + 'Z');
  }

  static convertImage(item: ObjectListItem): { id: string; src: string; alt: string; width: number; height: number; name: string; url: string; caption: string; format: string; } | null {
    if (item.Image === undefined) {
      return null;
    }
    const { ImageID, Caption, Format } = item.Image;
    const { Url, AltText, Width, Height, Name, Link } = item.Image.HtmlImage;
    const image = {
      id: ImageID,
      src: Url,
      alt: AltText,
      width: Width,
      height: Height,
      name: Name,
      url: Link,
      caption: Caption,
      format: RequestSearch.ImageFormat[Format]
    };
    return image;
  }

  static convertObjectItems(items: ObjectListItem[]): AdditionalItems {
    const objectItem: AdditionalItems = {};
    const emptyDate = '0001-01-01 00:00:00.000';
    for (const item of items) {
      const type: string = RequestSearch.AdditionalInfo[item.Type];

      if (item.DateFrom !== emptyDate && item.DateUntil !== emptyDate) {
        let a = this.convertTime(item.DateFrom).getTime();
        let b = this.convertTime(item.DateUntil).getTime();

        a = Math.min(a, b);
        b = Math.max(a, b);

        const now = Date.now();
        if (now < a || now > b) {
          continue;
        }
      }

      let genericItems: GenericItem[] | undefined;
      switch (type) {
        case 'news':
          objectItem.news = objectItem.news || [];
          if (
            isNotEmptyString(item.Headline) ||
            isNotEmptyString(item.Description)
          ) {
            objectItem.news.push({
              headline: item.Headline,
              description: item.Description,
              language: item.Language,
              created: RequestSearch.convertTime(item.CreateDate)
            } as NewsItem);
          }
          break;
        case 'product':
          objectItem.product = objectItem.product || [];
          objectItem.product.push({
            index: parseInt(item.SortNo, 10),
            headline: item.Headline,
            subHeadline: item.SubHeadline,
            description: item.Description,
            longText: item.LongText,
            country: item.Country,
            language: item.Language,
            created: RequestSearch.convertTime(item.CreateDate),
            image: RequestSearch.convertImage(item)
          } as ProductItem);
          break;
        case 'contact':
          objectItem.contact = objectItem.contact || [];
          objectItem.contact.push({
            firstName: item.Firstname,
            lastName: item.Surname,
            description: item.Description,
            email: item.Email,
            department: item.Department,
            tel: item.Phone,
            fax: item.Fax,
            country: item.Country,
            language: item.Language,
            created: RequestSearch.convertTime(item.CreateDate),
            position: {
              lat: parseFloat(item.YCoord),
              lng: parseFloat(item.XCoord)
            },
            image: RequestSearch.convertImage(item)
          } as ContactItem);
          break;
        case 'custom':
          objectItem.custom = objectItem.custom || [];
          objectItem.custom.push({
            index: parseInt(item.SortNo, 10),
            headline: item.Headline,
            subHeadline: item.SubHeadline,
            description: item.Description,
            longText: item.LongText,
            country: item.Country,
            language: item.Language,
            created: RequestSearch.convertTime(item.CreateDate),
            position: {
              lat: parseFloat(item.YCoord),
              lng: parseFloat(item.XCoord)
            },
            image: RequestSearch.convertImage(item)
          } as CustomItem);
          break;
        case 'home':
          objectItem.home = objectItem.home || [];
          objectItem.home.push({
            headline: item.Headline,
            description: item.Description,
            longText: item.LongText,
            country: item.Country,
            language: item.Language,
            created: RequestSearch.convertTime(item.CreateDate),
            position: {
              lat: parseFloat(item.YCoord),
              lng: parseFloat(item.XCoord)
            }
          } as HomeItem);
          break;
        case 'freeText':
          objectItem.freeText = objectItem.freeText || [];
          if (
            isNotEmptyString(item.Headline) ||
            isNotEmptyString(item.Description)
          ) {
            objectItem.freeText.push({
              category: item.Category,
              headline: item.Headline,
              subHeadline: item.SubHeadline,
              description: item.Description.replace(/&gt;/g, '>').replace(
                /&lt;/g,
                '<'
              ),
              longText: item.LongText,
              country: item.Country,
              language: item.Language,
              created: RequestSearch.convertTime(item.CreateDate),
              image: RequestSearch.convertImage(item)
            } as FreeTextItem);
          }
          break;
        case 'print':
          objectItem.print = objectItem.print || [];
          objectItem.print.push({
            headline: item.Headline,
            description: item.Description
          } as PrintItem);
          break;
        case 'video':
          objectItem.video = objectItem.video || [];
          objectItem.video.push({
            headline: item.Headline,
            description: item.Description,
            url: item.Url,
            image: RequestSearch.convertImage(item)
          } as VideoItem);
          break;
        default:
          if (type === 'chapter') {
            genericItems = objectItem.chapter = objectItem.chapter || [];
          }
          if (type === 'coupon') {
            genericItems = objectItem.coupon = objectItem.coupon || [];
          }
          if (type === 'infoText') {
            genericItems = objectItem.infoText = objectItem.infoText || [];
          }
          if (type === 'partner') {
            genericItems = objectItem.partner = objectItem.partner || [];
          }
          if (type === 'promotion') {
            genericItems = objectItem.promotion = objectItem.promotion || [];
          }
          if (
            genericItems !== undefined &&
            (isNotEmptyString(item.Headline) ||
              isNotEmptyString(item.Description))
          ) {
            genericItems.push({
              headline: item.Headline,
              description: item.Description,
              image: RequestSearch.convertImage(item)
            } as GenericItem);
          }
          break;
      }
    }
    return objectItem;
  }

  static convertAddressItemMap(item: AddressItem): SearchEntry {
    const config = getConfig();
    const {
      CompanyName,
      CompanyNameAddon1,
      CompanyNameAddon2,
    } = item.BasicData.Address;

    // Source (this is correct!):
    // Core\Presentation\AlteLeipziger\Models\AgencyDetailsModel.cs (line 36)
    const name = CompanyName.replace(/&amp;/g, '&');
    const nameAddon1 = (CompanyNameAddon1 || '').replace(/&amp;/g, '&');
    const nameAddon2 = (CompanyNameAddon2 || '').replace(/&amp;/g, '&');

    const ekomiId: number | null = null;
    const ekomiToken: string | null = null;

    const result = {
      type: 'Result',
      address: {
        zip: item.BasicData.Address.Zip,
        streetNo:
          item.BasicData.Address.Street + (item.BasicData.Address.HouseNo ? ' ' + item.BasicData.Address.HouseNo : ''),
        isoCountry: item.BasicData.Address.Country,
        city: item.BasicData.Address.City,
        cityPart: item.BasicData.Address.CityPart,
        country: item.BasicData.Address.Country,
        distance: item.BasicData.Geo.Distance,
        name,
        nameAddon1,
        nameAddon2
      },
      contact: {
        email: RequestSearch.extractValues(item.BasicData.Contact, ['Email']),
        fax: RequestSearch.extractValues(item.BasicData.Contact, ['Fax']),
        mobile: RequestSearch.extractValues(item.BasicData.Contact, ['Mobile']),
        tel: RequestSearch.extractValues(item.BasicData.Contact, ['Phone']),
        url: RequestSearch.extractValues(item.BasicData.Contact, [
          'Url',
          'Url2',
          'Url3'
        ])
      },
      position: {
        lat: parseFloat(item.BasicData.Geo.YCoord || '0'),
        lng: parseFloat(item.BasicData.Geo.XCoord || '0'),
        alt: undefined
      },
      businessHours: normalizeOpeningHours(item.BasicData.MemoItems),
      branchCodes: item.BasicData.BranchListElements.map(
        entry => entry.BranchCode
      ),
      customerId: item.BasicData.Identifiers.ProviderForeignKey,
      ekomiId,
      ekomiToken,
      id: item.BasicData.Identifiers.YMID,
      idDecoded: item.BasicData.Identifiers.YMIDDecoded,
      logo: item.BasicData.Images.find(image => image.Type === IMAGE_TYPE.LOGO),
      images: item.BasicData.Images.filter(
        image => image.Type !== IMAGE_TYPE.LOGO
      ).map(image => image.HtmlImage.Url),
      information: item.BasicData.BranchListElements.filter(
        entry =>
          (config.detailsView.infoWhitelist as string[]).indexOf(
            entry.BranchCode
          ) > -1
      ).map(entry => entry.BranchCode),
      title:
        item.BasicData.Address.CompanyName +
        (item.BasicData.Address.CompanyNameAddon1
          ? ' ' + item.BasicData.Address.CompanyNameAddon1
          : ''),
      additions: RequestSearch.convertObjectItems(
        item.BasicData.ObjectListItems
      )
    } as ResultEntry;
    return result;
  }

  static convertTileMap(tile: AddressTile): SearchEntry {
    return {
      type: 'Cluster',
      id: tile.ClusterID,
      idDecoded: tile.ClusterID
    } as ClusterEntry;
  }

  static handleServerErrors(result: ResponseError): void {
    if (result.InternalError) {
      throw new Error(result.InternalError);
    } else if (result.PublicReport) {
      throw new Error(result.PublicReport);
    }
  }

  static generateSearchEntries(
    result: AddressSearchResult,
    fitBounds = true
  ): SearchEntries {
    if (result.PublicReport) {
      return {
        type: 'SearchEntries',
        entries: [],
        geocoding: [],
        error: result.PublicReport,
        fitBounds: false
      };
    }
    const geocoding = result.GeoItems.map(
      RequestGeocoder.convertGeocoderItemMap
    );
    const [geocoded] = geocoding;

    const center: ym.provider.LatLngLiteral = {
      lat: parseFloat(result.SearchData.Location.YCoord),
      lng: parseFloat(result.SearchData.Location.XCoord),
      alt: undefined
    };
    if (geocoded && latLngNeqZero(geocoded.position)) {
      center.lat = geocoded.position.lat;
      center.lng = geocoded.position.lng;
      center.alt = geocoded.position.alt;
    }

    // const ekomiTokens = [
    //   'sf000015f82f80a11dd5',
    //   'sf000025f855fb1c2462',
    //   'sf000035f8560e3e0751',
    //   'sf000045f8560e3e0751',
    //   'sf000055f8560e3e0751',
    //   'sf000065f8560e3e0751',
    // ];
    // for (const item of result.AddressItems) {
    //   if (ekomiTokens.length) {
    //     item.BasicData.Address.CompanyNameAddon3 = ekomiTokens.shift() as string;
    //   } else {
    //     break;
    //   }
    // }

    const resultTiles = result.AddressTiles.map(RequestSearch.convertTileMap);
    const resultItems = result.AddressItems.map(RequestSearch.convertAddressItemMap);
    const topVisible = getConfig().searchRequest.topVisible || getConfig().searchRequest.top;

    resultItems.splice(topVisible);

    return {
      type: 'SearchEntries',
      entries: resultTiles.concat(resultItems),
      geocoding,
      center,
      error: result.PublicReport,
      fitBounds
    };
  }

  static get url(): string {
    return `${ym.options.rootUrl}api_js/V2/AddressSearches.asmx/`;
  }

  static get credentials(): Credentials {
    return {
      SystemPartner: ym.options.name || '',
      SecurityID: ym.options.securityID || '',
      Channel: ym.options.channel || ''
    };
  }

  static get jsonpOptions(): PJSONPOptions {
    return {
      prefix: 'YMFF__'
    };
  }

  private static extractValues(object: any, keys: string[]) {
    const result: string[] = [];
    for (const key of keys) {
      const value = object[key];
      if (isNotEmptyString(value)) {
        result.push(value);
      }
    }
    return result;
  }
}
